import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getAnswers,
  getModalState,
  getQuestion,
  setAnswers,
  setCurrentSubject,
  setModalState,
  setQuestion,
  setQuestionID,
} from '../../globals/application';
import {
  ARRANGEINORDER,
  DRAGDROP,
  FIB,
  MATCHINGPAIRS,
  MCQ,
  MULTISELECT,
  SWIPELEFTRIGHT,
  TRUE_FALSE,
} from '../../globals/constants';
import ContentNetworkLayer from '../../helpers';
import DiagramModal from '../DiagramModal';
import { subjectUsesMath } from '../EquationModal';
import Question from '../QuestionTypes/Question';
import QuestionPreview from '../QuestionPreview';
import {
  ArrangeInOrder,
  DragAndDrop,
  MatchingPairs,
  MultipleChoice,
  MultiSelect,
  SwipeLeftRight,
  TrueOrFalse,
} from '../QuestionTypes';

const TYPE_LABELS = {
  [MCQ]: 'Multiple Choice',
  [ARRANGEINORDER]: 'Arrange In Order',
  [FIB]: 'Fill In The Blanks',
  [TRUE_FALSE]: 'True / False',
  [MULTISELECT]: 'Multi-Select',
  [SWIPELEFTRIGHT]: 'Swipe Left / Right',
  [DRAGDROP]: 'Drag and Drop',
  [MATCHINGPAIRS]: 'Matching Pairs',
};

// Map internal type to preview type
const TYPE_MAP = {
  [MCQ]: 'MCQ',
  [MULTISELECT]: 'MultiSelect',
  [TRUE_FALSE]: 'True/False',
  [FIB]: 'FIB',
  [ARRANGEINORDER]: 'Matching',
  [SWIPELEFTRIGHT]: 'SwipeLeftRight',
  [DRAGDROP]: 'DragDrop',
  [MATCHINGPAIRS]: 'MatchingPairs',
};

const HINTS = {
  [FIB]: 'Type "blank" where you want the answer inserted. e.g. "The capital of France is blank."',
  [ARRANGEINORDER]: 'Enter items in the correct order — they will be randomized for the student.',
  [SWIPELEFTRIGHT]:
    'Student will swipe the question card left or right. Customize the labels for each direction.',
  [DRAGDROP]:
    'Define categories on the left and items that match each category. Students drag items to their matching category.',
  [MATCHINGPAIRS]:
    'Enter terms and their matching definitions. Students will tap to connect matching pairs.',
};

function answersValid(questionType, answers, questionText) {
  if (!answers && answers !== false) return false;
  if (questionType === TRUE_FALSE) return answers === 'true' || answers === 'false';
  if (questionType === SWIPELEFTRIGHT) {
    return answers?.correctAnswer === 'left' || answers?.correctAnswer === 'right';
  }
  if (questionType === DRAGDROP) {
    // Only categorize mode now (matching categories with items)
    if (answers?.mode !== 'categorize') return false;
    return (
      answers.zones?.length >= 2 &&
      answers.zones.every(z => z.label?.trim()) &&
      answers.items?.length >= 2 &&
      answers.items.every(it => it.text?.trim())
    );
  }
  if (questionType === MATCHINGPAIRS) {
    // Matching pairs: need at least 2 complete pairs (both term and definition filled)
    // Filter to pairs that have any content, then check if those are complete
    if (!Array.isArray(answers)) return false;
    const filledPairs = answers.filter(p => p.term?.trim() || p.definition?.trim());
    const completePairs = filledPairs.filter(p => p.term?.trim() && p.definition?.trim());
    return completePairs.length >= 2;
  }
  if (!Array.isArray(answers) || answers.length === 0) return false;
  if (questionType === MCQ || questionType === FIB) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === MULTISELECT) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === ARRANGEINORDER) {
    return answers.length >= 2 && answers.every(a => a.ans?.trim());
  }
  return true;
}

export default function AddQuestion() {
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);
  const question = useSelector(getQuestion);
  const modalState = useSelector(getModalState);

  const [questionType, setQuestionType] = useState('');
  const [complexity, setComplexity] = useState('Easy');
  const [subjects, setSubjects] = useState([]);
  const [code, setCode] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [showDiagramModal, setShowDiagramModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if selected subject uses math symbols
  const selectedSubject = useMemo(
    () => subjects.find(s => s.code === parseInt(code)),
    [subjects, code]
  );
  const showEquationButton = useMemo(
    () => subjectUsesMath(selectedSubject?.name),
    [selectedSubject]
  );

  // Clear answers + question text when type changes
  useEffect(() => {
    dispatch(setAnswers([]));
    dispatch(setQuestion(''));
  }, [questionType, dispatch]);

  // Check if answers are missing correct answer selection
  const missingCorrectAnswer =
    (questionType === MCQ || questionType === FIB || questionType === MULTISELECT) &&
    Array.isArray(answers) &&
    answers.length > 0 &&
    answers.some(a => a.answer?.trim()) && // Has at least one answer with text
    !answers.some(a => a.is_correct); // But none marked as correct

  const isValid =
    questionType &&
    question.trim() &&
    code &&
    answersValid(questionType, answers, question) &&
    (questionType !== FIB || question.toLowerCase().includes('blank'));

  const fibMissingBlank =
    questionType === FIB && question.trim() && !question.toLowerCase().includes('blank');

  const handleFile = f => {
    setFileName(f.name);
    const reader = new FileReader();
    reader.readAsDataURL(f);
    reader.onload = () => setFile(reader.result);
    reader.onerror = () => toast.error('Could not read file.');
  };

  useEffect(() => {
    ContentNetworkLayer.getSubjects()
      .then(res => setSubjects(res.subjects))
      .catch(() => toast.error('Could not load subjects.'));
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);

    // Debug: Log answers before processing
    console.log('🔍 [AddQuestion] Raw answers from Redux:', answers);
    console.log('🔍 [AddQuestion] Question type:', questionType);

    const question_id = Math.floor(100000 + Math.random() * 900000);
    dispatch(setQuestionID(question_id));

    const payload = {
      question: question,
      question_id,
      question_type: questionType,
      complexity,
      code: parseInt(code),
    };

    // True/False: backend reads `answer` field, not `answers`
    if (questionType === TRUE_FALSE) {
      payload.answer = answers;
    } else if (questionType === SWIPELEFTRIGHT) {
      // SwipeLeftRight: backend reads `answer` field with 'left' or 'right'
      // Also send left_label and right_label
      payload.answer = answers?.correctAnswer;
      payload.left_label = answers?.leftLabel || 'Incorrect';
      payload.right_label = answers?.rightLabel || 'Correct';
    } else if (questionType === DRAGDROP) {
      // DragDrop: transform to backend format with drag_drop_items
      // Backend expects: { item_text, correct_position, zone_id, zone_label }
      const dragDropItems = answers.items.map((item, index) => {
        const zone = answers.zones.find(z => z.id === item.zoneId);
        return {
          item_text: item.text,
          correct_position: index + 1,
          zone_id: item.zoneId,
          zone_label: zone?.label || '',
        };
      });
      payload.drag_drop_items = JSON.stringify(dragDropItems);
    } else if (questionType === MATCHINGPAIRS) {
      // MatchingPairs: transform to answers array format
      // Backend expects array of { answer (term or definition), order (links pairs) }
      // First half are terms, second half are definitions
      // The 'order' field links matching pairs together
      // Only include complete pairs (both term and definition filled)
      const completePairs = answers.filter(p => p.term?.trim() && p.definition?.trim());
      const matchingAnswers = [];
      completePairs.forEach((pair, idx) => {
        // Add term
        matchingAnswers.push({
          answer: pair.term,
          order: idx + 1,
        });
      });
      completePairs.forEach((pair, idx) => {
        // Add definition
        matchingAnswers.push({
          answer: pair.definition,
          order: idx + 1,
        });
      });
      console.log('🔍 [AddQuestion] MatchingPairs transformed:', matchingAnswers);
      payload.answers = JSON.stringify(matchingAnswers);
    } else {
      payload.answers = JSON.stringify(answers);
    }

    if (file) {
      payload.file = file;
      payload.file_id = Math.floor(100000 + Math.random() * 900000);
      payload.filename = `question-image-${question_id}`;
    }

    console.log('📤 [AddQuestion] Payload:', JSON.stringify(payload, null, 2));

    try {
      await ContentNetworkLayer.createQuestion(payload);
      toast.success('Question added!');
      if (modalState) dispatch(setModalState(false));
      setQuestionType('');
      setCode('');
      setFile(null);
      setFileName('');
    } catch (error) {
      console.error('❌ [AddQuestion] Error:', error);
      console.error('❌ [AddQuestion] Response:', error.response?.data);
      const errorMsg = error.response?.data?.errors 
        ? Object.values(error.response.data.errors).flat().join(', ')
        : error.response?.data?.message || 'Failed to add question. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // If in modal mode, don't use the two-column layout
  if (modalState) {
    return (
      <div className="form-card">
        <div className="form-card-header">
          <h2>Add Question</h2>
          <p>Create a quiz question with answer choices.</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Type + Subject row */}
          <div className="field-row">
            <div className="field-group">
              <label className="field-label">Question Type</label>
              <select
                className="field-select"
                value={questionType}
                onChange={e => setQuestionType(e.target.value)}
              >
                <option value="">Select type...</option>
                {Object.entries(TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label">
                Subject <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                className="field-select"
                value={code}
                onChange={e => {
                  setCode(e.target.value);
                  dispatch(setCurrentSubject(e.target.value));
                }}
              >
                <option value="">Select subject...</option>
                {subjects.map(s => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Complexity tier — powers the app's level-selection feature */}
          <div className="field-group">
            <label className="field-label">Complexity</label>
            <div className="complexity-selector">
              {['Easy', 'Intermediate', 'Advance'].map(level => (
                <button
                  key={level}
                  type="button"
                  className={`complexity-btn complexity-btn--${level.toLowerCase()} ${
                    complexity === level ? 'active' : ''
                  }`}
                  onClick={() => setComplexity(level)}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {HINTS[questionType] && (
            <div className="info-banner">
              <span className="info-icon">💡</span>
              <span>{HINTS[questionType]}</span>
            </div>
          )}

          {fibMissingBlank && (
            <div
              className="info-banner"
              style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}
            >
              <span className="info-icon">⚠️</span>
              <span>Add the word "blank" in your question where the answer should go.</span>
            </div>
          )}

          {questionType && <Question showEquationButton={showEquationButton} />}

          {questionType === MCQ || questionType === FIB ? (
            <MultipleChoice showEquationButton={showEquationButton} />
          ) : questionType === MULTISELECT ? (
            <MultiSelect showEquationButton={showEquationButton} />
          ) : questionType === ARRANGEINORDER ? (
            <ArrangeInOrder showEquationButton={showEquationButton} />
          ) : questionType === TRUE_FALSE ? (
            <TrueOrFalse />
          ) : questionType === SWIPELEFTRIGHT ? (
            <SwipeLeftRight />
          ) : questionType === DRAGDROP ? (
            <DragAndDrop showEquationButton={showEquationButton} />
          ) : questionType === MATCHINGPAIRS ? (
            <MatchingPairs showEquationButton={showEquationButton} />
          ) : null}

          {/* Warning: Missing correct answer */}
          {missingCorrectAnswer && (
            <div
              className="info-banner"
              style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}
            >
              <span className="info-icon">⚠️</span>
              <span>
                <strong>Required:</strong> Please select which answer is correct by clicking the
                "Correct" radio button next to the right answer.
              </span>
            </div>
          )}

          <div className="form-footer">
            <span />
            <div className="form-footer-actions">
              <button type="submit" className="btn btn-primary" disabled={!isValid || loading}>
                {loading ? 'Submitting...' : 'Submit Question'}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="questions-layout">
      {/* Left side - Form */}
      <div className="form-card">
        <div className="form-card-header">
          <h2>Add Question</h2>
          <p>Create a quiz question with answer choices.</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Type + Subject row */}
          <div className="field-row">
            <div className="field-group">
              <label className="field-label">Question Type</label>
              <select
                className="field-select"
                value={questionType}
                onChange={e => setQuestionType(e.target.value)}
              >
                <option value="">Select type...</option>
                {Object.entries(TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label">
                Subject <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                className="field-select"
                value={code}
                onChange={e => {
                  setCode(e.target.value);
                  dispatch(setCurrentSubject(e.target.value));
                }}
              >
                <option value="">Select subject...</option>
                {subjects.map(s => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Complexity tier — powers the app's level-selection feature */}
          <div className="field-group">
            <label className="field-label">Complexity</label>
            <div className="complexity-selector">
              {['Easy', 'Intermediate', 'Advance'].map(level => (
                <button
                  key={level}
                  type="button"
                  className={`complexity-btn complexity-btn--${level.toLowerCase()} ${
                    complexity === level ? 'active' : ''
                  }`}
                  onClick={() => setComplexity(level)}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Hint banner */}
          {HINTS[questionType] && (
            <div className="info-banner">
              <span className="info-icon">💡</span>
              <span>{HINTS[questionType]}</span>
            </div>
          )}

          {/* FIB: warn if "blank" missing */}
          {fibMissingBlank && (
            <div
              className="info-banner"
              style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}
            >
              <span className="info-icon">⚠️</span>
              <span>Add the word "blank" in your question where the answer should go.</span>
            </div>
          )}

          {/* Question textarea */}
          {questionType && <Question showEquationButton={showEquationButton} />}

          {/* Answer inputs */}
          {questionType === MCQ || questionType === FIB ? (
            <MultipleChoice showEquationButton={showEquationButton} />
          ) : questionType === MULTISELECT ? (
            <MultiSelect showEquationButton={showEquationButton} />
          ) : questionType === ARRANGEINORDER ? (
            <ArrangeInOrder showEquationButton={showEquationButton} />
          ) : questionType === TRUE_FALSE ? (
            <TrueOrFalse />
          ) : questionType === SWIPELEFTRIGHT ? (
            <SwipeLeftRight />
          ) : questionType === DRAGDROP ? (
            <DragAndDrop showEquationButton={showEquationButton} />
          ) : questionType === MATCHINGPAIRS ? (
            <MatchingPairs showEquationButton={showEquationButton} />
          ) : null}

          {/* Warning: Missing correct answer */}
          {missingCorrectAnswer && (
            <div
              className="info-banner"
              style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}
            >
              <span className="info-icon">⚠️</span>
              <span>
                <strong>Required:</strong> Please select which answer is correct by clicking the
                "Correct" radio button next to the right answer.
              </span>
            </div>
          )}

          {/* File upload */}
          <div className="field-group">
            <label className="field-label">Image (optional)</label>
            <div
              className="upload-area"
              onDragOver={e => {
                e.preventDefault();
                e.currentTarget.classList.add('upload-area--drag');
              }}
              onDragLeave={e => e.currentTarget.classList.remove('upload-area--drag')}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.classList.remove('upload-area--drag');
                const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
                if (dropped) handleFile(dropped);
              }}
            >
              <input
                type="file"
                id="question-file"
                hidden
                accept="image/*"
                onChange={e => handleFile(e.target.files[0])}
                onClick={e => (e.target.value = null)}
              />
              <label htmlFor="question-file" className="upload-btn">
                <span>+</span> Choose image
              </label>
              <button
                type="button"
                className="upload-btn"
                onClick={() => setShowDiagramModal(true)}
              >
                <span>✏️</span> Draw Diagram
              </button>
              <span className="upload-dnd-hint">or drag &amp; drop here</span>
              {fileName && (
                <span className="upload-filename">
                  {fileName}{' '}
                  <button
                    type="button"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                    }}
                    onClick={() => {
                      setFile(null);
                      setFileName('');
                    }}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
          </div>

          <div className="form-footer">
            <span />
            <div className="form-footer-actions">
              <button type="submit" className="btn btn-primary" disabled={!isValid || loading}>
                {loading ? 'Submitting...' : 'Submit Question'}
              </button>
            </div>
          </div>
        </form>

        <DiagramModal
          isOpen={showDiagramModal}
          onClose={() => setShowDiagramModal(false)}
          onInsert={({ dataUrl, name }) => {
            setFile(dataUrl);
            setFileName(name);
          }}
        />
      </div>

      {/* Right side - Preview */}
      <div className="preview-panel">
        <QuestionPreview
          questionType={TYPE_MAP[questionType]}
          questionText={question}
          answers={answers}
          subjectName={selectedSubject?.name}
          image={file}
        />
      </div>
    </div>
  );
}
